/**
 * AssistantConversation — one KI-Hofassistent conversation, surface-agnostic
 * (FR-015).
 *
 * The full turn lifecycle extracted from ``KiAssistantTab`` (#1656 → FR-015)
 * so the side-panel tab and the /agent workspace page render the SAME
 * implementation: budget badge + composer gating, async-202 turn
 * (``AwaitingTurn`` marker + thinking indicator + rotating status lines
 * superseded by live ``chat:assistant.step`` labels), inline failed-turn +
 * retry rows (#1790/#1834), clear-conversation confirm, cited-document
 * deep-links, read-on-view.
 *
 * The TARGET conversation comes in as a prop instead of being derived here:
 * - the panel tab passes the most recently active ``agent_assistant`` thread;
 * - the /agent page passes the selected conversation, plus ``ensureThreadId``
 *   (create-on-first-message) and ``onTurnAccepted`` (URL sync after the ack).
 * Callers switching between conversations MUST key this component by thread id
 * so per-turn local state (awaiting/failed markers) never bleeds across
 * conversations; the remount re-derivation effect (#1834) restores an
 * in-flight turn's indicator from the persisted rows.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ListRestart, RotateCcw, Wallet } from 'lucide-react';
import {
  Button,
  Composer,
  ConfirmationModal,
  Marker,
  Message,
  MessageScroller,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@hofsync/design-system';
import {
  useChatMessages,
  useClearChatConversation,
  useMarkChatRead,
} from '../../hooks/useChatQueries';
import {
  useAssistantBudget,
  usePostAssistantTurn,
} from '../../hooks/useAssistantQueries';
import { useAssistantStepEvents } from '../../hooks/useAssistantStepEvents';
import { useAssistantTurnFailedEvents } from '../../hooks/useAssistantTurnFailedEvents';
import { KiMessageBubble } from './tabs/KiMessageBubble';
import { withDateSplitters } from './DateSplitter';
import type { ChatMessage, ChatThread } from '../../services/chatApi';
import './tabs/KiAssistantTab.css';
import './MessageBubble.css';

// Budget-badge low-balance band (#1771): the badge turns amber once the
// remaining balance drops below this fraction of the monthly limit, and red at
// or below zero (the composer's isBlocked cutover). The absolute fallback is
// used only in the degenerate case where the server reports no monthly limit.
const LOW_BALANCE_FRACTION = 0.2;
const LOW_BALANCE_FALLBACK_EUR = 5;
// How long each rotating "thinking" status line is shown (ms).
const STATUS_ROTATE_MS = 2500;
// Client-side safety backstop (#1790): if neither a reply, an error bubble, nor
// a chat:assistant.turn_failed signal arrives within this window (≥ the server's
// ASSISTANT_TURN_TIMEOUT_S=90 + retry budget), clear the awaiting state into an
// error row — covers a backend pod dying mid-turn.
const SAFETY_TIMEOUT_MS = 120_000;

// Local "a turn is running, its reply/error hasn't landed yet" marker (#1790).
// The async-202 turn no longer resolves the reply on the HTTP response, so the
// thinking indicator is keyed off this instead of the mutation's isPending.
interface AwaitingTurn {
  threadId: string;
  userMessageId: string;
  // Clear the indicator once an agent message with sent_at STRICTLY after this
  // appears (covers the reply AND a persisted error bubble; robust to dropped
  // SSE frames). On a retry this is the newest already-loaded message, not the
  // (older) re-used user message — otherwise the stale error bubble settles it.
  sinceIso: string;
}

export interface AssistantConversationProps {
  /** The conversation to render. ``undefined`` = none yet (first-ever turn on
   * the panel, or the /agent "new conversation" state). */
  thread: ChatThread | undefined;
  /** FR-015 create-on-first-message: called when a turn is sent with no
   * ``thread`` — returns the freshly created conversation's id (the turn then
   * posts with an explicit ``thread_id``). Absent → the turn posts without one
   * and the backend resolves/creates (legacy panel behaviour). */
  ensureThreadId?: () => Promise<string | undefined>;
  /** Notifies the surface which conversation a 202-accepted turn landed on
   * (URL sync on the /agent page). */
  onTurnAccepted?: (threadId: string) => void;
  /** Empty-conversation canvas content. Default: the panel's coming-soon copy. */
  emptyState?: React.ReactNode;
  /** Leading content of the header row (left of the clear button + budget
   * badge, which keeps its ``margin-left: auto`` right alignment). The /agent
   * page passes its conversation title here so title and badge share ONE row;
   * when set, the header renders even for an empty conversation. */
  headerLeading?: React.ReactNode;
  className?: string;
}

export const AssistantConversation: React.FC<AssistantConversationProps> = ({
  thread,
  ensureThreadId,
  onTurnAccepted,
  emptyState,
  headerLeading,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const messagesQuery = useChatMessages(thread?.thread_id);
  const budgetQuery = useAssistantBudget();
  const turn = usePostAssistantTurn();
  const markRead = useMarkChatRead(thread?.thread_id);
  const clearConversation = useClearChatConversation(thread?.thread_id);
  const [draft, setDraft] = useState('');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const lastReadRef = useRef<string | null>(null);
  // Async-turn lifecycle (#1790): the awaiting marker, per-message inline error
  // rows (keyed by failed_user_message_id), and a transient composer-level error
  // for pre-ack failures (network / 4xx / 409 in_flight — nothing persisted).
  const [awaiting, setAwaiting] = useState<AwaitingTurn | null>(null);
  // ``source:'marker'`` tags a row re-derived on remount from the durable
  // ``failed_reason`` marker (#1834) — a NO-persisted-bubble failure. Unlike a
  // live ``turn_failed`` row (which sits alongside a persisted agent error
  // bubble and must survive next to it), a marker row has no bubble, so a later
  // agent message under it is a real reply and the row must be reconciled away
  // (see the reconcile effect below). Live/timeout rows leave ``source`` unset.
  const [failedTurns, setFailedTurns] = useState<
    Record<string, { reason: string; source?: 'marker' }>
  >({});
  const [sendError, setSendError] = useState<string | null>(null);
  const awaitingDerivedRef = useRef(false);
  // #1905 create-on-first-message: remember a conversation minted by
  // ``ensureThreadId`` so a retry after a PRE-ACK failure (network / 409
  // in_flight / 5xx — no message, so the response can't recover the id, and the
  // URL never switched) reuses it instead of minting a SECOND empty
  // conversation. Per component instance: a real conversation switch remounts
  // the pane (fresh ref), so it can't leak across conversations. A 404
  // ``not_found`` clears it (the server says the thread is gone → re-mint, don't
  // loop on a dead id). On accepted/blocked the URL switch remounts the pane and
  // discards this instance's ref anyway.
  const mintedThreadIdRef = useRef<string | null>(null);

  // Oldest-first so date splitters appear chronologically; soft-deleted
  // rows are hidden on the user side (admin keeps the tombstone).
  const messagesOldestFirst = useMemo(() => {
    if (!messagesQuery.data) return [] as ChatMessage[];
    return messagesQuery.data
      .filter((m) => !m.deleted_at)
      .slice()
      .sort(
        (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
      );
  }, [messagesQuery.data]);

  const budget = budgetQuery.data;
  // Block only once the server has reported a non-positive balance —
  // while the budget query is still loading we keep the composer live.
  const isBlocked = budget !== undefined && budget.remaining_eur <= 0;

  // State-aware budget badge (#1771): neutral while the balance is healthy,
  // amber when it drops into the low band (< 20% of the monthly limit), red at
  // or below zero — the SAME cutover as the composer's ``isBlocked``. Exposed
  // via ``data-state`` so the colour is driven by a token class, not a
  // hardcoded pill.
  let badgeState: 'neutral' | 'low' | 'blocked' | null = null;
  if (budget !== undefined) {
    const lowThreshold =
      budget.monthly_limit_eur > 0
        ? budget.monthly_limit_eur * LOW_BALANCE_FRACTION
        : LOW_BALANCE_FALLBACK_EUR;
    badgeState = isBlocked
      ? 'blocked'
      : budget.remaining_eur <= lowThreshold
        ? 'low'
        : 'neutral';
  }

  // "Thinking" subtitle while a turn is pending (#1771 sub-item 4 + #1776).
  // Until the first live step event of the current turn arrives, a client-side
  // list of localized generic status lines rotates. Once the backend's
  // ``chat:assistant.step`` SSE (published per ReAct tool call) delivers a
  // step, its tool-grounded label supersedes the rotation for the rest of the
  // turn; both reset the moment the turn resolves. Computed inline (not
  // memoised) so it re-localizes when ``t`` changes; the array is tiny.
  const rawStatusLines = t('chat.ki.statusLines', { returnObjects: true });
  const statusLines =
    Array.isArray(rawStatusLines) && rawStatusLines.length > 0
      ? (rawStatusLines as string[])
      : [t('chat.ki.thinking', 'Thinking…')];
  const [statusIndex, setStatusIndex] = useState(0);
  const [liveStep, setLiveStep] = useState<{ tool: string } | null>(null);
  // Since the async-202 cutover (#1802) the mutation's isPending dies with the
  // fast 202 ack — long before the reply lands. The thinking indicator, composer
  // gate, and step-event gating therefore key off ``showThinking`` (isPending OR
  // the local awaiting marker), not isPending alone.
  const isAwaiting = awaiting !== null;
  const showThinking = turn.isPending || isAwaiting;
  useEffect(() => {
    if (!showThinking) {
      setStatusIndex(0);
      setLiveStep(null); // reset on resolve — the next turn rotates fresh
      return;
    }
    const id = window.setInterval(() => {
      setStatusIndex((i) => i + 1);
    }, STATUS_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [showThinking]);
  const currentStatus = statusLines[statusIndex % statusLines.length];

  // Live step events (#1776). The hook is a dumb subscription — display
  // gating lives here: only while THIS surface's turn is pending (the event is
  // a tenant-wide broadcast, other tabs receive it too), and only for the
  // conversation when it is known. On a first-ever turn the thread is
  // minted server-side inside the POST (not derivable client-side), so an
  // unknown thread accepts the step rather than dropping it.
  useAssistantStepEvents({
    onStep: (evt) => {
      if (!showThinking) return;
      if (thread?.thread_id && evt.thread_id !== thread.thread_id) return;
      setLiveStep({ tool: evt.step.tool });
    },
  });

  // Terminal turn-failure signal (#1790). Bind an inline "Antwort fehlgeschlagen"
  // + retry row to the failed user message; also drop the thinking indicator if
  // we were awaiting THAT message (belt-and-braces for a dropped error bubble).
  // Own-thread only (the event is a tenant-wide broadcast — other tabs see it).
  useAssistantTurnFailedEvents({
    onTurnFailed: (evt) => {
      if (thread?.thread_id && evt.thread_id !== thread.thread_id) return;
      setFailedTurns((prev) => ({
        ...prev,
        [evt.failed_user_message_id]: { reason: evt.reason },
      }));
      setAwaiting((cur) =>
        cur && cur.userMessageId === evt.failed_user_message_id ? null : cur,
      );
    },
  });

  // Clear the awaiting marker once the settling message lands (#1790). An agent
  // message with sent_at strictly after ``sinceIso`` is either the real reply or
  // the backend's persisted error bubble — both end the "thinking" state. Then
  // refetch the budget so the badge reflects the background debit.
  const budgetRefetch = budgetQuery.refetch;
  useEffect(() => {
    if (!awaiting) return;
    const settled = messagesOldestFirst.some(
      (m) =>
        m.author_kind === 'agent' &&
        new Date(m.sent_at).getTime() > new Date(awaiting.sinceIso).getTime(),
    );
    if (!settled) return;
    setAwaiting(null);
    budgetRefetch?.();
  }, [awaiting, messagesOldestFirst, budgetRefetch]);

  // Safety backstop (#1790): a backend pod dying mid-turn emits no reply, no
  // error bubble, and no turn_failed. After ~120s convert the awaiting marker
  // into an inline error row so the user is never stuck on a silent spinner.
  useEffect(() => {
    if (!awaiting) return;
    const failedId = awaiting.userMessageId;
    const id = window.setTimeout(() => {
      setFailedTurns((prev) => ({ ...prev, [failedId]: { reason: 'timeout' } }));
      setAwaiting(null);
    }, SAFETY_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [awaiting]);

  // Reconcile a *no-real-answer* error row against a late real reply (#1790 +
  // #1834). Two row kinds are heuristic/no-bubble and MUST clear once a genuine
  // agent reply lands so they never sit over a correct answer nor offer a retry
  // that would re-run a PAID turn:
  //   • ``reason:'timeout'`` — the 120s safety timer is a heuristic; a merely-slow
  //     turn (budget 90s + retry) or a dropped ``chat:message.new`` frame can trip
  //     it, yet still answer.
  //   • ``source:'marker'`` — a remount-derived durable-marker row (#1834) has NO
  //     persisted error bubble, so a later agent message under it is provably a
  //     real reply (a successful retry), not an error bubble.
  // Live ``turn_failed`` rows (no source, non-timeout) are authoritative failures
  // left untouched: their retry affordance survives alongside the backend's
  // persisted error bubble (itself an agent message that would otherwise clear them).
  useEffect(() => {
    const timeoutIds = Object.entries(failedTurns)
      .filter(([, v]) => v.reason === 'timeout' || v.source === 'marker')
      .map(([id]) => id);
    if (timeoutIds.length === 0) return;
    const answered = timeoutIds.filter((uid) => {
      const userMsg = messagesOldestFirst.find((m) => m.message_id === uid);
      return (
        userMsg &&
        messagesOldestFirst.some(
          (m) =>
            m.author_kind === 'agent' &&
            new Date(m.sent_at).getTime() > new Date(userMsg.sent_at).getTime(),
        )
      );
    });
    if (answered.length === 0) return;
    setFailedTurns((prev) => {
      const next = { ...prev };
      for (const uid of answered) delete next[uid];
      return next;
    });
  }, [failedTurns, messagesOldestFirst]);

  // Remount re-derivation (#1790 + #1834): a reload mid-turn loses the local
  // awaiting marker. On the first loaded message set, when the last message is a
  // user message (no reply after it):
  //   • If it carries a durable terminal-failure marker (#1834), the turn already
  //     failed with no persisted error bubble (client-mint-failure /
  //     unmounted-consumer path). Restore the failed + retry affordance IMMEDIATELY
  //     — no phantom ≤120s "Thinking…" spinner, no mislabeled reason:'timeout'.
  //     The marker is authoritative regardless of the message age.
  //   • Otherwise, if it is fresh (< safety window), we are genuinely mid-turn —
  //     put the thinking indicator back.
  // Runs once per mount (ref-gated); a genuine remount is a new component instance
  // with a fresh ref — including a conversation switch on the /agent page, which
  // re-keys this component.
  useEffect(() => {
    if (awaitingDerivedRef.current) return;
    if (messagesOldestFirst.length === 0) return;
    awaitingDerivedRef.current = true;
    const last = messagesOldestFirst[messagesOldestFirst.length - 1];
    if (last.author_kind !== 'user') return;
    const markerReason = last.failed_reason;
    if (markerReason) {
      setFailedTurns((prev) => ({
        ...prev,
        [last.message_id]: { reason: markerReason, source: 'marker' },
      }));
      return;
    }
    const ageMs = Date.now() - new Date(last.sent_at).getTime();
    if (ageMs < 0 || ageMs > SAFETY_TIMEOUT_MS) return;
    setAwaiting({
      threadId: last.thread_id,
      userMessageId: last.message_id,
      sinceIso: last.sent_at,
    });
  }, [messagesOldestFirst]);

  // Tool → localized label; unknown tools degrade to the generic fallback
  // (``i18n.exists`` guard — a bare dynamic ``t()`` would render the raw key).
  const stepLabel = liveStep
    ? i18n.exists(`chat.ki.steps.${liveStep.tool}`)
      ? t(`chat.ki.steps.${liveStep.tool}`)
      : t('chat.ki.steps.fallback', 'Working on your request…')
    : null;

  // Read-on-view (Bug 3, #1724). A mounted conversation == a viewed one (the
  // panel renders only the active tab; the /agent page renders only the
  // selected conversation). Mark the latest message read whenever it changes —
  // covers both the reply that lands while mounted and a reply that arrived
  // while hidden. ``mark_read_up_to`` is an additive, idempotent watermark
  // write (sent_at <= latest), so ``thread.unread_count`` (and with it the FAB
  // badge summed in ChatProvider) drops to zero.
  //
  // Gate on ``unread_count`` so we don't issue a redundant write on every
  // remount/tab-toggle, nor on the user's own just-sent message (the server
  // only counts non-user messages as unread). ``chat:message.new`` SSE
  // invalidates both the threads and messages queries, so ``unread_count``
  // is fresh by the time the agent reply surfaces.
  const latestMessageId =
    messagesOldestFirst.length > 0
      ? messagesOldestFirst[messagesOldestFirst.length - 1].message_id
      : null;
  const unreadCount = thread?.unread_count ?? 0;
  const markReadMutate = markRead.mutate;
  useEffect(() => {
    if (!latestMessageId || !thread?.thread_id || unreadCount <= 0) return;
    if (lastReadRef.current === latestMessageId) return;
    lastReadRef.current = latestMessageId;
    markReadMutate(latestMessageId);
  }, [latestMessageId, thread?.thread_id, unreadCount, markReadMutate]);

  // Map a pre-ack mutation error (nothing persisted) to a localized composer
  // hint. A 409 in_flight (concurrent-turn lock, #1802) gets a distinct message;
  // everything else (network, 4xx, 5xx) is the generic "couldn't send".
  const preAckErrorMessage = (err: unknown): string => {
    const status = (err as { response?: { status?: number } } | null)?.response
      ?.status;
    if (status === 409) {
      return t(
        'chat.ki.error.inFlight',
        'A request is already running. Please wait a moment.',
      );
    }
    return t(
      'chat.ki.error.sendFailed',
      'Message could not be sent. Please try again.',
    );
  };

  // Record the awaiting marker from a 202 ``accepted`` ack (fresh send or retry).
  // The clear boundary is the newest already-loaded message OR the acked user
  // message, whichever is later — so a retry (which re-uses the older user row)
  // does not settle instantly against the stale error bubble from the failed
  // attempt. Clears any inline error row for the (retried) message.
  const startAwaiting = (message: ChatMessage) => {
    const newestLoaded = messagesOldestFirst.length
      ? messagesOldestFirst[messagesOldestFirst.length - 1].sent_at
      : message.sent_at;
    const sinceIso =
      new Date(newestLoaded).getTime() > new Date(message.sent_at).getTime()
        ? newestLoaded
        : message.sent_at;
    setAwaiting({
      threadId: message.thread_id,
      userMessageId: message.message_id,
      sinceIso,
    });
    setFailedTurns((prev) => {
      if (!(message.message_id in prev)) return prev;
      const next = { ...prev };
      delete next[message.message_id];
      return next;
    });
  };

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isBlocked || showThinking) return;
    const sourceUrl = `${window.location.pathname}${window.location.search}`;
    // Optimistic clear (Bug 1, #1724): the ``/v1/assistant/turn`` POST is a
    // multi-second ReAct loop, so clearing only in ``onSuccess`` leaves the
    // sent text sitting in the box under the "Thinking…" indicator. Clear
    // synchronously; restore the draft on error (only if the user hasn't
    // started typing something new) so a failed turn keeps its retry text.
    setSendError(null);
    setDraft('');
    // FR-015 create-on-first-message: with no conversation yet, let the surface
    // mint one (the /agent page creates it inside the selected project) so the
    // turn posts with an explicit thread_id. A mint failure surfaces like any
    // pre-ack failure — draft restored, nothing persisted.
    // Prefer an already-minted conversation id (#1905): after a pre-ack failure
    // the URL never switched to the minted thread, so ``thread`` is still
    // undefined here — reuse the remembered id rather than minting a SECOND empty
    // conversation on retry. A 404 ``not_found`` cleared the ref (see onError).
    let threadId = thread?.thread_id ?? mintedThreadIdRef.current ?? undefined;
    if (!threadId && ensureThreadId) {
      try {
        threadId = await ensureThreadId();
        mintedThreadIdRef.current = threadId ?? null;
      } catch {
        setDraft((cur) => (cur.trim() ? cur : trimmed));
        setSendError(preAckErrorMessage(null));
        return;
      }
    }
    turn.mutate(
      { body: trimmed, sourceUrl, locale: i18n.language, threadId },
      {
        onSuccess: (data) => {
          // 202 accepted → re-key the thinking indicator off the awaiting marker
          // (blocked/ok/error land their message synchronously or via SSE).
          if (data.status === 'accepted') startAwaiting(data.message);
          // Select/sync to the conversation the turn landed on for ANY response
          // carrying a thread id (#1905): accepted (202) AND blocked (200) both
          // persist a message onto the minted thread, so the URL must switch in
          // both — previously only accepted did, stranding a blocked first turn
          // on the ``new:`` URL and letting a retry mint a second conversation.
          // ``onTurnAccepted`` self-guards ``chatParam !== threadId`` (a no-op
          // when already selected) and is absent on the panel, so calling it on
          // every success path is safe/idempotent.
          if (data.message?.thread_id) onTurnAccepted?.(data.message.thread_id);
        },
        // Pre-ack failure (network, 4xx, 409 in_flight): NOTHING was persisted
        // for this send → restore the draft AND surface a visible error (fixes
        // the previously-silent failure). Keep the minted-id ref so a retry
        // reuses the conversation — EXCEPT on a 404 ``not_found`` (the server
        // says the minted thread is gone): clear it so the retry re-mints
        // instead of looping on a dead id (#1905).
        onError: (err) => {
          const status = (err as { response?: { status?: number } } | null)
            ?.response?.status;
          if (status === 404) mintedThreadIdRef.current = null;
          setDraft((cur) => (cur.trim() ? cur : trimmed));
          setSendError(preAckErrorMessage(err));
        },
      },
    );
  };

  // Bound retry (#1790): resend the persisted failed user message by id so the
  // backend re-uses that row (exactly ONE user message stays in the thread).
  const handleRetry = (failedMessage: ChatMessage) => {
    if (isBlocked || showThinking) return;
    const sourceUrl = `${window.location.pathname}${window.location.search}`;
    setSendError(null);
    turn.mutate(
      {
        retryOfMessageId: failedMessage.message_id,
        sourceUrl,
        locale: i18n.language,
        threadId: failedMessage.thread_id,
      },
      {
        onSuccess: (data) => {
          if (data.status === 'accepted') startAwaiting(data.message);
        },
        // The inline error row stays (nothing new persisted) so the user can
        // retry again; surface the transient composer error too.
        onError: (err) => setSendError(preAckErrorMessage(err)),
      },
    );
  };

  // Clear conversation (#1732): a non-destructive, server-side hide of
  // the user's own conversation (admin/admin-MCP keep the full history).
  // The button only appears once there is something to clear, and is
  // gated behind a confirm dialog. Close the dialog when the mutation
  // settles; the hook invalidates the message + thread queries so the
  // canvas empties and the FAB unread badge (#1735) drops.
  const hasMessages = messagesOldestFirst.length > 0;
  const handleClearConfirm = () => {
    if (!thread?.thread_id) return;
    clearConversation.mutate(undefined, {
      onSuccess: () => setConfirmClearOpen(false),
      onError: () => setConfirmClearOpen(false),
    });
  };

  // Cited-document badge navigation (#1757). Deep-link the main app to the
  // document (``&mode=edit`` opens the DocumentsPage preview overlay). This is
  // client-side navigation, so only the router ``<Outlet>`` swaps — the chat
  // panel (mounted above the Outlet in ProtectedLayout) survives it; the
  // /agent page navigates away like any in-app link.
  const handleOpenDocument = useCallback(
    (documentId: string) => {
      navigate(`/documents?doc=${encodeURIComponent(documentId)}&mode=edit`);
    },
    [navigate],
  );

  // Budget-badge tone (#1876): the DS Marker pill colours itself from the same
  // neutral / low / blocked cutover, now expressed as the conversation-layer
  // tone (neutral / warning / danger) rather than a bespoke pill class.
  const badgeTone =
    badgeState === 'blocked'
      ? 'danger'
      : badgeState === 'low'
        ? 'warning'
        : 'neutral';

  return (
    <div className={`chat-ki ${className ?? ''}`} data-testid="chat-ki-tab">
      {(headerLeading !== undefined || hasMessages || budget !== undefined) && (
        <div className="chat-ki__header" data-testid="chat-ki-header">
          {headerLeading}
          {hasMessages && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="chat-ki__clear"
                    data-testid="chat-ki-clear"
                    aria-label={t('chat.ki.clear', 'Clear conversation')}
                    onClick={() => setConfirmClearOpen(true)}
                  >
                    <ListRestart size={16} aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('chat.ki.clear', 'Clear conversation')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {budget !== undefined && (
            <Marker
              variant="pill"
              tone={badgeTone}
              className="chat-ki__badge"
              data-testid="chat-ki-badge"
              data-state={badgeState ?? undefined}
            >
              <Wallet size={13} aria-hidden="true" />
              {t('chat.ki.badgeLabel', '{{amount}} € left', {
                amount: budget.remaining_eur.toFixed(2),
              })}
            </Marker>
          )}
        </div>
      )}
      {/* Streaming message canvas on the DS MessageScroller (#1876): pins to the
          bottom as a turn streams, backs off when the reader scrolls up. */}
      <MessageScroller
        autoScroll
        className="chat-ki__messages"
        data-testid="chat-messages"
      >
        <div className="chat-ki__messages-inner">
          {messagesOldestFirst.length === 0 &&
            !messagesQuery.isLoading &&
            !showThinking &&
            (emptyState ?? (
              <p className="chat-ki__coming-soon">
                {t(
                  'chat.ki.comingSoon',
                  'Features will be unlocked gradually this season',
                )}
              </p>
            ))}
          {withDateSplitters(messagesOldestFirst, (msg) => (
            <React.Fragment key={msg.message_id}>
              <KiMessageBubble message={msg} onOpenDocument={handleOpenDocument} />
              {/* Inline failed-turn row (#1790), bound to the failed user message:
                  a localized "Antwort fehlgeschlagen" + a retry that resends by id
                  (no duplicate user message). Rendered as an incoming (agent) row. */}
              {msg.author_kind === 'user' && failedTurns[msg.message_id] && (
                <Message role="assistant">
                  <div
                    className="chat-ki__error-row"
                    role="alert"
                    data-testid="chat-ki-error-row"
                  >
                    <span className="chat-ki__error-text">
                      {t('chat.ki.error.turnFailed', 'Answer failed')}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="chat-ki__retry"
                      data-testid="chat-ki-retry"
                      onClick={() => handleRetry(msg)}
                      disabled={isBlocked || showThinking}
                    >
                      <RotateCcw size={14} aria-hidden="true" />
                      {t('chat.ki.error.retry', 'Try again')}
                    </Button>
                  </div>
                </Message>
              )}
            </React.Fragment>
          ))}
          {showThinking && (
            <Message role="assistant" data-author-kind="agent">
              <div className="chat-ki__thinking" data-testid="chat-ki-thinking">
                {/* Screen readers get ONE static announcement; the rotating
                    visible subtitle rides the DS streaming Marker (aria-hidden,
                    so re-announcing every 2.5s rotation doesn't spam the SR
                    queue). */}
                <span
                  className="chat-ki__sr-only"
                  role="status"
                  data-testid="chat-ki-thinking-sr"
                >
                  {t('chat.ki.thinking', 'Thinking…')}
                </span>
                <Marker
                  streaming
                  aria-hidden="true"
                  className="chat-ki__thinking-subtitle"
                  data-testid="chat-ki-thinking-subtitle"
                >
                  {stepLabel ?? currentStatus}
                </Marker>
              </div>
            </Message>
          )}
        </div>
      </MessageScroller>
      <div className="chat-ki__composer">
        {isBlocked && (
          <p className="chat-ki__blocked-hint" data-testid="chat-ki-blocked">
            {t('chat.ki.blockedHint', 'Balance used up — contact Support')}
          </p>
        )}
        {sendError && (
          <p
            className="chat-ki__send-error"
            role="alert"
            data-testid="chat-ki-send-error"
          >
            {sendError}
          </p>
        )}
        {/* Composer input on the DS conversation layer (#1876): auto-grow +
            Enter-to-send, blocked (disabled) / in-flight (busy) gating. */}
        <Composer
          value={draft}
          onValueChange={(value) => {
            setDraft(value);
            if (sendError) setSendError(null);
          }}
          onSubmit={handleSend}
          disabled={isBlocked}
          busy={showThinking}
          labels={{
            placeholder: t(
              'chat.ki.placeholderActive',
              'Ask the KI-Hofassistent…',
            ),
            send: t('chat.input.send', 'Send'),
          }}
          sendButtonProps={{ 'data-testid': 'chat-ki-send' }}
        />
      </div>
      <ConfirmationModal
        isOpen={confirmClearOpen}
        title={t('chat.ki.clearConfirmTitle', 'Clear conversation?')}
        message={t(
          'chat.ki.clearConfirmMessage',
          'This hides the current conversation from your view. Your monthly balance is not refunded.',
        )}
        confirmLabel={t('chat.ki.clearConfirm', 'Clear')}
        cancelLabel={t('common.cancel', 'Cancel')}
        onConfirm={handleClearConfirm}
        onCancel={() => setConfirmClearOpen(false)}
        danger
        loading={clearConversation.isPending}
      />
    </div>
  );
};
